import { useMutation, useQuery } from '@apollo/client';
import { isNil, sortBy } from 'lodash';
import moment from 'moment';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Mutations, Queries, Subscriptions } from '@shared/api';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBMessage } from '@shared/scenes/chat/thread/containers/message';
import { ThreadContextProvider } from '@shared/scenes/chat/thread/containers/thread/provider';
import { useDispatch, useSelector, useStore } from '@shared/store';
import { usePrevious } from '@shared/util/hooks';
import { notNil } from '@shared/util/notNil';
import * as Actions from '../../store/actions';
import { MBReadMarker } from '../../store/actions';

const emptyArray = [];

interface Props {
  threadId: string;
  isFocused: boolean;
  channelSlug: string;
  onThreadUpdated?: () => void;
}

function useData(threadId: string) {
  const result = useQuery<MBAPI.CardChatThreadQuery, MBAPI.CardChatThreadQueryVariables>(
    Queries.card.chatThread,
    {
      variables: { id: threadId },
      fetchPolicy: 'cache-and-network',
    },
  );
  return result;
}

function useMessages(data: MBAPI.CardChatThreadQuery | undefined): MBAPI.ChatMessage[] {
  const messages = data?.thread?.messages;
  if (!messages) {
    return emptyArray;
  }
  return messages as MBAPI.ChatMessage[];
}

function useInitialReadAt(thread?: MBAPI.Thread | null): string | undefined {
  const [initialReadAt, setInitialReadAt] = useState<string>();
  useEffect(() => {
    if (!thread) {
      return;
    }

    const readAt = thread.memberships?.find(m => m.user.isMe)?.messagesViewedAt;
    const lastMessageCreatedAt = thread.messages?.[0]?.createdAt;
    // Note(fant): we don't set read at if all messages are
    // initially read. Why? We don't want the read marker to
    // appear when a message is send/received.
    if (!!readAt && moment(readAt).isBefore(lastMessageCreatedAt)) {
      setInitialReadAt(readAt);
    }
  }, [thread]);

  return initialReadAt;
}

function useLayoutMessages(
  messages: MBAPI.ChatMessage[],
  data: MBAPI.CardChatThreadQuery | undefined,
) {
  const dispatch = useDispatch();
  const initialReadAt = useInitialReadAt(data?.thread);

  const readMarkers: MBReadMarker[] = useMemo(() => {
    return (
      data?.thread?.memberships?.filter(notNil).map(m => ({
        readAt: m.messagesViewedAt ?? '',
        user: m.user,
      })) ?? []
    );
  }, [data?.thread?.memberships]);

  useEffect(() => {
    Actions.layoutMessages(
      messages,
      readMarkers,
      !!initialReadAt ? moment(initialReadAt) : null,
    )(dispatch);
  }, [messages, initialReadAt, readMarkers, dispatch]);
}

function useMarkMessagesAsRead(
  messages: MBAPI.ChatMessage[],
  threadId: string,
  isFocused: boolean,
  data: MBAPI.CardChatThreadQuery | undefined,
  refetch: () => Promise<any>,
  loading: boolean,
) {
  const readAt = data?.thread?.memberships?.find(m => m.user.isMe)?.messagesViewedAt;
  const lastMessageCreatedAt = messages[0]?.createdAt;

  const hasUnread = useMemo(() => {
    if (!lastMessageCreatedAt) {
      return false;
    }
    if (!readAt) {
      return true;
    }
    return moment(readAt).isBefore(lastMessageCreatedAt);
  }, [readAt, lastMessageCreatedAt]);
  const hadUnread = usePrevious(hasUnread);
  const wasFocused = usePrevious(isFocused);
  const shouldMarkAsRead = (() => {
    if (loading) {
      return false;
    }
    if (isFocused && hasUnread && !hadUnread) {
      return true;
    }
    if (!wasFocused && isFocused && hasUnread) {
      return true;
    }
    return false;
  })();

  const [markMessagesAsReadMutation] = useMutation<
    MBAPI.MarkThreadMessagesAsReadMutation,
    MBAPI.MarkThreadMessagesAsReadMutationVariables
  >(Mutations.thread.markMessagesAsRead);
  const markMessagesAsRead = useCallback(async () => {
    await markMessagesAsReadMutation({ variables: { threadId } });
    // TODO(fant): remove this when we can refetch
    // thread.userMetadata.messagesViewedAt through
    // MarkThreadMessagesAsReadMutation. Otherwise,
    // the chat thread component will call this fn all the time.
    await refetch();
  }, [markMessagesAsReadMutation, threadId, refetch]);

  useEffect(() => {
    if (shouldMarkAsRead) {
      markMessagesAsRead();
    }
  }, [shouldMarkAsRead, markMessagesAsRead, hasUnread, hadUnread]);
}

function useMessageSubscriptions(
  threadId: string,
  subscribeToMore: ReturnType<typeof useData>['subscribeToMore'],
) {
  const store = useStore();

  useEffect(() => {
    if (!threadId) {
      return;
    }

    const unsubscribeCreated = subscribeToMore<
      MBAPI.ThreadMessageCreatedSubscription,
      MBAPI.ThreadMessageCreatedSubscriptionVariables
    >({
      document: Subscriptions.thread.messageCreated,
      variables: { input: { threadId } },
      updateQuery(prevData, { subscriptionData }) {
        const message = subscriptionData.data.message;
        if (!prevData.thread) {
          return prevData;
        }

        const isMessageAlreadyAdded = prevData.thread.messages?.some(m => m.id === message.id);
        // Sometimes subscriptions are faster than the actual API response. When we
        // create new messages, it might be the case that they come here first. This
        // happens randomly. When that's the case, animations look bad (one layout
        // animation for the new message and one right afterwards when the delivery
        // status is updated by the callback for the API request). Therefore we don't
        // add those messages here. Instead we let the client wait a few more ms until
        // the API responds.
        const deliveryStatusState = store.getState().chatMessages.deliveryStatusFromMessageUuid;
        const isMessageBeingSentByClient =
          !isNil(message.meta.uuid) && deliveryStatusState[message.meta.uuid] === 'sending';

        if (isMessageAlreadyAdded || isMessageBeingSentByClient) {
          return prevData;
        }
        return {
          thread: {
            ...prevData.thread,
            // Note(fant): most of the time new messages should be at
            // the bottom of the thread. However, when deleting a message
            // a system message will appear at the old message's createdAt
            // so we need to sort the incoming message to the right place.
            messages: sortBy(
              [message, ...(prevData.thread?.messages ?? [])],
              m => -moment(m.createdAt).valueOf(),
            ),
          },
        };
      },
    });

    const unsubscribeUpdated = subscribeToMore<
      MBAPI.ThreadMessageUpdatedSubscription,
      MBAPI.ThreadMessageUpdatedSubscriptionVariables
    >({
      document: Subscriptions.thread.messageUpdated,
      variables: { input: { threadId } },
      updateQuery(prevData, { subscriptionData }) {
        const message = subscriptionData.data.message;
        if (!prevData.thread) {
          return prevData;
        }

        const prevMessages = prevData.thread.messages ?? [];
        return {
          thread: {
            ...prevData.thread,
            messages: prevMessages.map(prev => {
              return prev.id !== message.id ? prev : message;
            }),
          },
        };
      },
    });

    const unsubscribeDeleted = subscribeToMore<
      MBAPI.ThreadMessageDeletedSubscription,
      MBAPI.ThreadMessageDeletedSubscriptionVariables
    >({
      document: Subscriptions.thread.messageDeleted,
      variables: { input: { threadId } },
      updateQuery(prevData, { subscriptionData }) {
        const message = subscriptionData.data.message;
        if (!prevData.thread) {
          return prevData;
        }

        const prevMessages = prevData.thread.messages ?? [];
        return {
          thread: {
            ...prevData.thread,
            messages: prevMessages.filter(prev => prev.id !== message.id),
          },
        };
      },
    });

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeDeleted();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);
}

function useCachedMessages(
  channelSlug: string,
  messages: MBAPI.ChatMessage[],
): MBAPI.ChatMessage[] {
  const cachedMessageByUuid = useSelector(state => state.chatMessages.cachedMessages);
  const cachedMessageUuidsInChannel = useSelector(
    state => state.chatMessages.cachedMessageUuidsFromChannelSlug[channelSlug] ?? emptyArray,
  );

  return useMemo(() => {
    if (!cachedMessageUuidsInChannel) {
      return [];
    }
    const messageUuids = messages.map(m => m.meta.uuid);
    return cachedMessageUuidsInChannel
      .map(uuid => cachedMessageByUuid[uuid])
      .filter(message => !!message)
      .filter(message => !messageUuids.includes(message.meta.uuid));
  }, [messages, cachedMessageByUuid, cachedMessageUuidsInChannel]);
}

function useRenderedMessages(messages: MBAPI.ChatMessage[], channelSlug: string): ReactElement[] {
  return useMemo(
    () =>
      messages
        .map(message => (
          <MBMessage
            key={message.meta.id}
            message={message}
            channelSlug={channelSlug}
            isDM={false}
            isHidingLinkPreview
            previousMessage={messages[messages.length - 1]}
          />
        ))
        .reverse(),
    [messages, channelSlug],
  );
}

export function MBCardChatThread({ threadId, isFocused, channelSlug, onThreadUpdated }: Props) {
  const { data, refetch, loading, subscribeToMore } = useData(threadId);
  const messages = useMessages(data);
  useMarkMessagesAsRead(messages, threadId, isFocused, data, refetch, loading);
  useLayoutMessages(messages, data);
  useMessageSubscriptions(threadId, subscribeToMore);

  const threadSlug = `thread:${data?.thread?.id}`;
  const cachedMessages = useCachedMessages(threadSlug, messages);

  const renderedMessages = useRenderedMessages(messages, threadSlug);
  const renderedCachedMessages = useRenderedMessages(cachedMessages, threadSlug);

  useEffect(() => {
    onThreadUpdated?.();
  }, [cachedMessages, onThreadUpdated]);

  if (!data?.thread?.messages) {
    return <MBLoadingIndicator padding={2} fill />;
  }
  return (
    <ThreadContextProvider value={{ channelSlug }}>
      {renderedMessages}
      {renderedCachedMessages}
    </ThreadContextProvider>
  );
}
